import {
  TGlobalSearchActions,
  TGlobalSearchDynamicState,
  TGlobalSearchSubtypeState,
  GlobalSearchActionTypes,
  TFilterParams,
  TGlobalSearch,
  TGlobalSearchClientState
} from './types';
import { FilterTypes, SearchFiltersType } from '../types';
import _ from 'lodash';
import { MULTI_VALUE_TYPES } from '../constants';
import { encodeSearchParams } from '../../../libs/utils';
import { buildGlobalSearchQuery, generateSearchDynamicKeyByParams } from '../utils';

export const globalSearchActions: TGlobalSearch = {
  resetPagination() {
    return {
      type: GlobalSearchActionTypes.RESET_PAGINATION,
      payload: {}
    };
  },
  setLastSearch(search: string) {
    return {
      type: GlobalSearchActionTypes.SET_LAST_SEARCH,
      payload: {
        search
      }
    };
  },
  setTimezone(timezone: string) {
    return { type: GlobalSearchActionTypes.SET_TIMEZONE, payload: { timezone } };
  },

  setSearchResults(results: any, searchType: string, query: any) {
    return {
      type: GlobalSearchActionTypes.SET_SEARCH_RESULTS,
      payload: {
        results,
        searchType,
        query
      }
    };
  },
  paginate(page: number, searchType: string, isUserAction?: boolean) {
    return {
      type: GlobalSearchActionTypes.PAGINATE,
      payload: { page, searchType, isUserAction }
    };
  },
  setPaginationLimit(limit: number, searchType: string) {
    return {
      type: GlobalSearchActionTypes.SET_PAGINATION_LIMIT,
      payload: { limit, searchType }
    };
  },

  setSearchFailure(results: any[], searchType: string) {
    // todo
    return {
      type: GlobalSearchActionTypes.SEARCH_FAILURE,
      payload: {
        results,
        searchType
      }
    };
  },

  setSearchType(selectedType: string) {
    return {
      type: GlobalSearchActionTypes.SET_SEARCH_TYPE,
      payload: {
        selectedType
      }
    };
  },

  setSortedBy(resultType: string, sorted: any, isUserAction?: boolean) {
    return {
      type: GlobalSearchActionTypes.SET_SORTED_BY,
      payload: {
        resultType,
        sorted,
        page: 0,
        isUserAction
      }
    };
  },

  startSearch(type: string, searchTerm: string) {
    return {
      type: GlobalSearchActionTypes.START_SEARCH,
      payload: {
        type,
        searchTerm
      }
    };
  },

  // add/update/removing filters:
  addFilter(resultType: string, filterType: FilterTypes, filterPath: string, value: any) {
    return { type: GlobalSearchActionTypes.ADD_FILTER, payload: { resultType, filterType, filterPath, value } };
  },

  // add/update/removing filters:
  setFilters(resultType: string, filters: TFilterParams[], sorted: any[]) {
    return { type: GlobalSearchActionTypes.SET_FILTERS, payload: { resultType, filters, sorted } };
  },

  resetFilters(resultType: string) {
    return { type: GlobalSearchActionTypes.RESET_FILTERS, payload: { resultType } };
  },

  removeFilter(resultType: string, id: number) {
    return { type: GlobalSearchActionTypes.REMOVE_FILTER, payload: { resultType, id } };
  },

  updateFilter(resultType: string, id: number, newKey: string, newValue: any, filterType: FilterTypes) {
    return { type: GlobalSearchActionTypes.UPDATE_FILTER, payload: { resultType, id, newKey, newValue, filterType } };
  },

  fetchDynamicByParams(params: any) {
    return { type: GlobalSearchActionTypes.FETCH_DYNAMIC_BY_PARAMS, payload: { params } };
  },

  setDynamicStateByParams(params: any, state: TGlobalSearchDynamicState) {
    return { type: GlobalSearchActionTypes.SET_DYNAMIC_BY_PARAMS, payload: { params, state } };
  }
};

const BASE_STATUS_STATE = {
  loading: false,
  loaded: true,
  error: false
};

export const LOADING_STATE = { ...BASE_STATUS_STATE, loading: true };
export const LOADED_STATE = { ...BASE_STATUS_STATE, loaded: true };
export const ERROR_STATE = { ...BASE_STATUS_STATE, error: true };

export const INITIAL_RESULT_TYPE_STATE: TGlobalSearchSubtypeState = {
  page: 0,
  results: {
    total: 0,
    matches: []
  },
  sortField: '',
  filters: {},
  filterCount: 0,
  sorted: {},
  query: {}
};

export const INITIAL_STATE: TGlobalSearchClientState & Record<SearchFiltersType, TGlobalSearchSubtypeState> = {
  searchTerm: '',
  selectedType: undefined,
  lastSearch: '',
  ...BASE_STATUS_STATE,
  character: { ...INITIAL_RESULT_TYPE_STATE },
  guild: { ...INITIAL_RESULT_TYPE_STATE },
  team: { ...INITIAL_RESULT_TYPE_STATE },
  mythic_plus_runs: { ...INITIAL_RESULT_TYPE_STATE },
  raid_encounters: { ...INITIAL_RESULT_TYPE_STATE },
  timezone: null,
  dynamic: {},
  cleared: false
};

export default function globalSearchReducer(
  state: TGlobalSearchClientState = INITIAL_STATE,
  action: TGlobalSearchActions
): Partial<TGlobalSearchClientState> {
  switch (action.type) {
    case GlobalSearchActionTypes.SET_PAGINATION_LIMIT: {
      const { limit, searchType } = action.payload;
      const searchsearchTypeState = state[searchType];
      return {
        ...state,
        [searchType]: {
          ...searchsearchTypeState,
          limit
        }
      };
    }
    case GlobalSearchActionTypes.PAGINATE: {
      const { searchType, page } = action.payload;
      const searchsearchTypeState = state[searchType];

      return {
        ...state,
        [searchType]: {
          ...searchsearchTypeState,
          page
        }
      };
    }
    case GlobalSearchActionTypes.SET_SEARCH_RESULTS: {
      const { results, searchType, query } = action.payload;
      const searchTypeState = state[searchType];
      return {
        ...state,
        ...LOADED_STATE,
        [searchType]: { ...searchTypeState, results, query }
      };
    }

    case GlobalSearchActionTypes.SEARCH_FAILURE: {
      return { ...state, ...ERROR_STATE };
    }

    case GlobalSearchActionTypes.SET_LAST_SEARCH: {
      const { search } = action.payload;
      return { ...state, lastSearch: search };
    }

    case GlobalSearchActionTypes.SET_SEARCH_TYPE: {
      const { selectedType } = action.payload;
      return { ...state, selectedType };
    }

    case GlobalSearchActionTypes.SET_SORTED_BY: {
      const { resultType, sorted, page } = action.payload;
      return {
        ...state,
        [resultType]: {
          ...state[resultType],
          page,
          sorted
        }
      };
    }

    case GlobalSearchActionTypes.START_SEARCH: {
      // need to get type from state or
      const { searchTerm } = action.payload;
      const { selectedType } = state;
      const filters = {
        1: { id: 1, filterType: FilterTypes.TEXT, filterPath: 'name', value: { contains: searchTerm } }
      };
      return {
        ...INITIAL_STATE,
        ...LOADING_STATE,
        character: {
          ...INITIAL_RESULT_TYPE_STATE,
          filterCount: 1,
          filters
        },
        lastSearch: selectedType ? encodeSearchParams(buildGlobalSearchQuery(selectedType, _.values(filters))) : ''
      };
    }

    case GlobalSearchActionTypes.ADD_FILTER: {
      const { resultType, filterType, filterPath, value } = action.payload;
      const currentFilters = state[resultType];

      currentFilters.filterCount++;

      const { filters, filterCount } = currentFilters;

      const allFilters = {
        ...filters,
        [filterCount]: { id: filterCount, filterType, filterPath, value }
      };

      const isLoading = _.values(value).indexOf(undefined) === -1;
      return {
        ...state,
        ...(isLoading ? LOADING_STATE : {}),
        [resultType]: {
          ...currentFilters,
          filterCount,
          filters: allFilters,
          page: 0
        },
        lastSearch: encodeSearchParams(buildGlobalSearchQuery(resultType, _.values(allFilters)))
      };
    }

    // goes back to zero filters with just the new one added
    case GlobalSearchActionTypes.SET_FILTERS: {
      const { resultType, filters, sorted } = action.payload;
      let filterCount = 1;

      const resultFilters = {
        sorted,
        filterCount: _.size(filters),
        filters: filters.reduce((accum: any, item: TFilterParams) => {
          accum[filterCount] = {
            id: filterCount,
            optionId: item.optionId,
            filterType: item.type,
            filterPath: item.path,
            value: item.value
          };
          filterCount++;
          return accum;
        }, {})
      };

      return {
        ...state,
        ...LOADING_STATE,
        [resultType]: {
          ...state[resultType],
          ...resultFilters
        }
      };
    }

    case GlobalSearchActionTypes.RESET_FILTERS: {
      const { resultType } = action.payload;
      return { ...INITIAL_STATE, selectedType: resultType, cleared: true };
    }

    case GlobalSearchActionTypes.REMOVE_FILTER: {
      const { resultType, id } = action.payload;
      const currentFilters = { ...state[resultType] };
      const validValues = _.values(currentFilters.filters[id].value).filter((v: any) => v !== undefined);

      const newFilters = _.omit(currentFilters.filters, id);
      const isCleared = _.isEmpty(newFilters);

      return {
        ...state,
        ...(validValues.length > 0 ? LOADING_STATE : {}),
        [resultType]: {
          ...currentFilters,
          page: 0,
          filters: newFilters,
          ...(isCleared ? { sorted: {} } : {})
        },
        cleared: isCleared,
        lastSearch: encodeSearchParams(buildGlobalSearchQuery(resultType, _.values(currentFilters.filters)))
      };
    }

    case GlobalSearchActionTypes.UPDATE_FILTER: {
      const { resultType, id, newKey, newValue, filterType } = action.payload;
      const currentFilters = state[resultType];

      if (_.isObject(newValue)) {
        _.set(currentFilters.filters, `${id}.value.${newKey}`, {
          ..._.get(currentFilters.filters, `${id}.value.${newKey}`, {}),
          ...newValue
        });
      } else {
        if (id in currentFilters.filters && !MULTI_VALUE_TYPES.has(filterType)) {
          _.unset(currentFilters.filters, `${id}.value`);
        }

        _.set(currentFilters.filters, `${id}.value.${newKey}`, newValue);
      }

      return {
        ...state,
        ...LOADING_STATE,
        [resultType]: {
          ...currentFilters,
          page: 0
        },
        lastSearch: encodeSearchParams(buildGlobalSearchQuery(resultType, _.values(currentFilters.filters)))
      };
    }
    case GlobalSearchActionTypes.RESET_PAGINATION: {
      return {
        ...state,
        character: { ...state.character, page: 0 },
        guild: { ...state.guild, page: 0 },
        team: { ...state.team, page: 0 },
        mythic_plus_runs: { ...state.mythic_plus_runs, page: 0 },
      };
    }
    case GlobalSearchActionTypes.SET_TIMEZONE: {
      const { timezone } = action.payload;
      return { ...state, timezone };
    }
    case GlobalSearchActionTypes.FETCH_DYNAMIC_BY_PARAMS: {
      const { params } = action.payload;
      const dynamicKey = generateSearchDynamicKeyByParams(params);
      return {
        ...state,
        dynamic: { ...state.dynamic, [dynamicKey]: { ...state.dynamic[dynamicKey], loading: true } }
      };
    }
    case GlobalSearchActionTypes.SET_DYNAMIC_BY_PARAMS: {
      const { params, state: dynamicState } = action.payload;
      const dynamicKey = generateSearchDynamicKeyByParams(params);
      return {
        ...state,
        dynamic: { ...state.dynamic, [dynamicKey]: { ...state.dynamic[dynamicKey], ...dynamicState } }
      };
    }
    default:
      return state;
  }
}
